/**
 * ポータル: マイページ（船員・陸上社員の本人ページ）
 * タレントマネジメント風: 顔（アバター）・所属・数字（補償休日/乗船/出勤）→
 * 基本情報・連絡先・緊急連絡先・資格・スキル・自己紹介。本人がその場で編集できる。
 */
import { useCallback, useEffect, useState } from 'react';
import { BottomNav } from '../../components/BottomNav';
import { PIcon } from '../../components/PIcon';
import { apiFetch } from '../../services/api';
import { Avatar, PORTAL_ACCENT, PORTAL_ACCENT_DARK, PORTAL_ACCENT_SOFT, inputStyle } from './ui';

interface ProfileData {
  user: { id: string; user_name: string; employee_code: string; user_role: string; role_label: string; joined_at: string | null };
  assignment: { vessel_name: string; position: string | null } | null;
  profile: {
    kana: string | null; birth_date: string | null; blood_type: string | null; hire_date: string | null;
    phone: string | null; email: string | null; address: string | null;
    emergency_name: string | null; emergency_phone: string | null;
    licenses: string[]; skills: string[]; bio: string | null;
  };
  stats: { comp_balance: number; onboard_days_year: number; office_days_month: number };
}

const card: React.CSSProperties = { background: '#fff', borderRadius: 14, border: '1px solid #e3e6ea', padding: '15px 16px', marginBottom: 12 };
const sectionTitle: React.CSSProperties = { fontSize: 13.5, fontWeight: 800, color: '#0c5f59', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 7 };
const rowLabel: React.CSSProperties = { width: 96, flexShrink: 0, fontSize: 12.5, color: '#50545b', fontWeight: 600, paddingTop: 2 };
const rowValue: React.CSSProperties = { fontSize: 14.5, color: '#1a1c1f', fontWeight: 600, flex: 1, wordBreak: 'break-word' };

export default function PortalProfilePage() {
  const [data, setData] = useState<ProfileData | null>(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    apiFetch<{ data: ProfileData }>('/portal/profile/me', {}, { noRedirect: true })
      .then(r => setData(r.data)).catch(() => { /* noop */ });
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    if (!data) return;
    const p = data.profile;
    setForm({
      kana: p.kana ?? '', birth_date: p.birth_date ?? '', blood_type: p.blood_type ?? '', hire_date: p.hire_date ?? '',
      phone: p.phone ?? '', email: p.email ?? '', address: p.address ?? '',
      emergency_name: p.emergency_name ?? '', emergency_phone: p.emergency_phone ?? '',
      licenses: p.licenses.join('、'), skills: p.skills.join('、'), bio: p.bio ?? '',
    });
    setEdit(true); setMsg('');
  };

  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await apiFetch<{ data: ProfileData }>('/portal/profile/me', {
        method: 'PUT',
        body: JSON.stringify({
          ...form,
          licenses: form['licenses']?.split(/[、,]/).map(s => s.trim()).filter(Boolean) ?? [],
          skills: form['skills']?.split(/[、,]/).map(s => s.trim()).filter(Boolean) ?? [],
        }),
      });
      setData(r.data); setEdit(false); setMsg('保存しました');
    } catch (e) { setMsg(e instanceof Error ? e.message : '保存に失敗しました'); }
    finally { setBusy(false); }
  };

  const F = (key: string, placeholder = '', type = 'text') => (
    <input type={type} style={{ ...inputStyle, padding: '9px 11px' }} placeholder={placeholder}
      value={form[key] ?? ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
  );
  const Row = ({ label, value, editNode }: { label: string; value: React.ReactNode; editNode?: React.ReactNode }) => (
    <div style={{ display: 'flex', alignItems: edit ? 'center' : 'flex-start', gap: 8, padding: '7px 0', borderBottom: '1px solid #f1f3f5' }}>
      <span style={rowLabel}>{label}</span>
      {edit && editNode ? <span style={{ flex: 1 }}>{editNode}</span> : <span style={rowValue}>{value || <span style={{ color: '#a8adb4', fontWeight: 500 }}>未登録</span>}</span>}
    </div>
  );

  if (!data) {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: '#f6f7f8' }}>
        <div style={{ background: '#fff', borderBottom: '1px solid #e3e6ea' }}><div className="app-safe-band" /><div style={{ padding: '4px 16px 12px', fontSize: 19, fontWeight: 800 }}>マイページ</div></div>
        <p style={{ textAlign: 'center', color: '#50545b', marginTop: 40, fontSize: 14 }}>読み込み中...</p>
        <BottomNav />
      </div>
    );
  }

  const p = data.profile;
  const stats = [
    { label: '補償休日 残', value: `${Number.isInteger(data.stats.comp_balance) ? data.stats.comp_balance : data.stats.comp_balance.toFixed(1)}日` },
    { label: '今年の乗船', value: `${data.stats.onboard_days_year}日` },
    { label: '今月の出勤', value: `${data.stats.office_days_month}日` },
  ];

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: '#f6f7f8' }}>
      {/* ヘッダー */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e3e6ea', flexShrink: 0 }}>
        <div className="app-safe-band" aria-hidden="true" />
        <div style={{ padding: '4px 16px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 19, fontWeight: 800, color: '#1a1c1f', flex: 1 }}>マイページ</span>
          {msg && <span style={{ fontSize: 12, color: PORTAL_ACCENT_DARK, fontWeight: 700 }}>{msg}</span>}
          {edit ? (
            <>
              <button onClick={() => setEdit(false)} disabled={busy}
                style={{ padding: '9px 14px', borderRadius: 9, border: '1px solid #c8cdd3', background: '#fff', color: '#3d4248', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                やめる
              </button>
              <button onClick={() => void save()} disabled={busy}
                style={{ padding: '9px 18px', borderRadius: 9, border: 'none', background: PORTAL_ACCENT, color: '#fff', fontSize: 13.5, fontWeight: 800, cursor: 'pointer' }}>
                {busy ? '保存中...' : '保存する'}
              </button>
            </>
          ) : (
            <button onClick={startEdit}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, border: `1.5px solid ${PORTAL_ACCENT}`, background: '#fff', color: PORTAL_ACCENT_DARK, fontSize: 13.5, fontWeight: 800, cursor: 'pointer' }}>
              <PIcon name="pen" size={13} /> 編集する
            </button>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 14px 90px' }}>
        {/* プロフィールヒーロー */}
        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 14 }}>
          <Avatar name={data.user.user_name} size={62} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, color: '#50545b', fontWeight: 600 }}>{p.kana ?? ''}</div>
            <div style={{ fontSize: 21, fontWeight: 900, color: '#1a1c1f', lineHeight: 1.3 }}>{data.user.user_name}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              <span style={{ fontSize: 11.5, fontWeight: 800, padding: '3px 10px', borderRadius: 20, background: PORTAL_ACCENT_SOFT, color: PORTAL_ACCENT_DARK }}>
                {data.user.role_label}
              </span>
              {data.assignment && (
                <span style={{ fontSize: 11.5, fontWeight: 800, padding: '3px 10px', borderRadius: 20, background: '#eef2f7', color: '#3d4248' }}>
                  {data.assignment.vessel_name}{data.assignment.position ? `・${data.assignment.position}` : ''}
                </span>
              )}
              <span style={{ fontSize: 11.5, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: '#f1f3f5', color: '#50545b' }}>
                {data.user.employee_code}
              </span>
            </div>
          </div>
        </div>

        {/* 数字 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
          {stats.map(s => (
            <div key={s.label} style={{ background: '#fff', border: '1px solid #e3e6ea', borderRadius: 12, padding: '12px 4px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#50545b', fontWeight: 700 }}>{s.label}</div>
              <div style={{ fontSize: 17, fontWeight: 900, color: PORTAL_ACCENT_DARK, marginTop: 3 }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* 基本情報 */}
        <div style={card}>
          <div style={sectionTitle}><PIcon name="idcard" size={15} color={PORTAL_ACCENT_DARK} /> 基本情報</div>
          <Row label="フリガナ" value={p.kana} editNode={F('kana', 'タナカ ミサキ')} />
          <Row label="生年月日" value={p.birth_date?.replace(/-/g, '/')} editNode={F('birth_date', '', 'date')} />
          <Row label="血液型" value={p.blood_type} editNode={F('blood_type', '例: A型')} />
          <Row label="入社年月日" value={p.hire_date?.replace(/-/g, '/')} editNode={F('hire_date', '', 'date')} />
        </div>

        {/* 連絡先 */}
        <div style={card}>
          <div style={sectionTitle}><PIcon name="chat" size={15} color={PORTAL_ACCENT_DARK} /> 連絡先</div>
          <Row label="電話" value={p.phone} editNode={F('phone', '090-0000-0000', 'tel')} />
          <Row label="メール" value={p.email} editNode={F('email', '', 'email')} />
          <Row label="住所" value={p.address} editNode={F('address')} />
        </div>

        {/* 緊急連絡先 */}
        <div style={card}>
          <div style={sectionTitle}><PIcon name="alert" size={15} color={PORTAL_ACCENT_DARK} /> 緊急連絡先</div>
          <Row label="名前・続柄" value={p.emergency_name} editNode={F('emergency_name', '例: 田中 一郎（父）')} />
          <Row label="電話" value={p.emergency_phone} editNode={F('emergency_phone', '', 'tel')} />
        </div>

        {/* 資格・免許 */}
        <div style={card}>
          <div style={sectionTitle}><PIcon name="filecheck" size={15} color={PORTAL_ACCENT_DARK} /> 資格・免許</div>
          {edit ? (
            <>
              {F('licenses', '例: 四級海技士（航海）、第一級海上特殊無線技士')}
              <div style={{ fontSize: 11.5, color: '#50545b', marginTop: 5 }}>「、」区切りで入力してください</div>
            </>
          ) : p.licenses.length === 0 ? (
            <div style={{ fontSize: 13, color: '#a8adb4' }}>未登録</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {p.licenses.map(l => (
                <span key={l} style={{ fontSize: 12.5, fontWeight: 700, padding: '5px 12px', borderRadius: 8, background: PORTAL_ACCENT_SOFT, color: PORTAL_ACCENT_DARK }}>{l}</span>
              ))}
            </div>
          )}
        </div>

        {/* スキル・得意なこと */}
        <div style={card}>
          <div style={sectionTitle}><PIcon name="pen" size={15} color={PORTAL_ACCENT_DARK} /> スキル・得意なこと</div>
          {edit ? (
            <>
              {F('skills', '例: 機関整備、Excel、フォークリフト')}
              <div style={{ fontSize: 11.5, color: '#50545b', marginTop: 5 }}>「、」区切りで入力してください</div>
            </>
          ) : p.skills.length === 0 ? (
            <div style={{ fontSize: 13, color: '#a8adb4' }}>未登録</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {p.skills.map(s => (
                <span key={s} style={{ fontSize: 12.5, fontWeight: 700, padding: '5px 12px', borderRadius: 8, background: '#eef2f7', color: '#3d4248' }}>{s}</span>
              ))}
            </div>
          )}
        </div>

        {/* 自己紹介 */}
        <div style={card}>
          <div style={sectionTitle}><PIcon name="user" size={15} color={PORTAL_ACCENT_DARK} /> 自己紹介</div>
          {edit ? (
            <textarea style={{ ...inputStyle, minHeight: 100 }} value={form['bio'] ?? ''}
              onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
              placeholder="経歴・ひとこと・休暇の過ごし方など、自由にどうぞ" />
          ) : (
            <div style={{ fontSize: 14, color: p.bio ? '#1a1c1f' : '#a8adb4', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
              {p.bio ?? '未登録'}
            </div>
          )}
        </div>

        <div style={{ fontSize: 11.5, color: '#8b9096', textAlign: 'center' }}>
          氏名・社員番号・配乗の変更は管理者（ユーザー管理）から行います
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
